import { useEffect, useState } from 'react'
import { CheckCircle, XCircle, Info, Warning, X } from '@phosphor-icons/react'

export type ToastType = 'success' | 'error' | 'info' | 'warning'

interface ToastProps {
  message: string
  type?: ToastType
  duration?: number
  onClose: () => void
}

export function Toast({ message, type = 'info', duration = 3000, onClose }: ToastProps) {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose()
    }, duration)

    return () => clearTimeout(timer)
  }, [duration, onClose])

  const icons = {
    success: <CheckCircle size={20} weight="fill" className="text-green-500" />,
    error: <XCircle size={20} weight="fill" className="text-red-500" />,
    info: <Info size={20} weight="fill" className="text-blue-500" />,
    warning: <Warning size={20} weight="fill" className="text-yellow-500" />
  }

  const bgColors = {
    success: 'bg-green-50 border-green-200',
    error: 'bg-red-50 border-red-200',
    info: 'bg-blue-50 border-blue-200',
    warning: 'bg-yellow-50 border-yellow-200'
  }

  const textColors = {
    success: 'text-green-900',
    error: 'text-red-900',
    info: 'text-blue-900',
    warning: 'text-yellow-900'
  }

  return (
    <div
      className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg border shadow-lg animate-slide-in-right ${bgColors[type]}`}
      style={{
        animation: 'slideInRight 0.3s ease-out'
      }}
    >
      {icons[type]}
      <p className={`text-sm font-medium ${textColors[type]}`}>{message}</p>
      <button
        onClick={onClose}
        className={`ml-2 ${textColors[type]} hover:opacity-70 transition-opacity`}
      >
        <X size={16} weight="bold" />
      </button>
    </div>
  )
}

// Hook para usar Toast
export function useToast() {
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null)

  const showToast = (message: string, type: ToastType = 'info') => {
    setToast({ message, type })
  }

  const hideToast = () => {
    setToast(null)
  }

  return {
    toast,
    showToast,
    hideToast,
    ToastComponent: toast ? (
      <Toast message={toast.message} type={toast.type} onClose={hideToast} />
    ) : null
  }
}

// Adicionar ao CSS global
const style = document.createElement('style')
style.textContent = `
  @keyframes slideInRight {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`
document.head.appendChild(style)
